import { horizontalEllipsis } from '@ipfabric/frontend-shared-assets/utils/characters';
import { FormDatepicker } from '@ipfabric/ui-components/FormDatepicker/FormDatepicker';
import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { format, parse } from 'date-fns';
import { useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { type Control, type UseFormSetValue, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { usePrevious } from 'utils/hooks/usePrevious';
import type {
  DiscoverySeedFormValues,
  NumericDaysLimit,
} from './DiscoverySeed';
import type { DiscoverySeedModel } from './model/DiscoverySeedModel';
import { setDiscoverySeed } from './model/command/setDiscoverySeed';

interface DiscoveryHistorySeedsProps {
  control: Control<DiscoverySeedFormValues, any>;
  model: DiscoverySeedModel;
  setValue: UseFormSetValue<DiscoverySeedFormValues>;
}

export const DiscoveryHistorySeeds = ({
  control,
  model,
  setValue,
}: DiscoveryHistorySeedsProps) => {
  const enabled = useWatch({ control, name: 'enabled' });
  const daysLimit = useWatch({ control, name: 'daysLimit' });
  const afterDate = useWatch({ control, name: 'afterDate' });

  useEffect(() => {
    model.dispatch(setDiscoverySeed, {
      data: {
        discoveryHistorySeeds: {
          afterDate:
            daysLimit === 'custom' && afterDate
              ? format(afterDate, 'yyyy-MM-dd')
              : null,
          daysLimit:
            daysLimit === 'custom' || daysLimit === 'all'
              ? null
              : Number(daysLimit),
          enabled,
        },
      },
    });
  }, [enabled, daysLimit, afterDate, model?.dispatch]);

  const prevInitialDiscoveryHistorySeeds = usePrevious(
    model?.initialDiscoveryHistorySeeds,
  );
  useEffect(() => {
    if (
      prevInitialDiscoveryHistorySeeds &&
      isEqual(
        prevInitialDiscoveryHistorySeeds,
        model?.initialDiscoveryHistorySeeds,
      )
    ) {
      return;
    }

    setValue('enabled', model?.initialDiscoveryHistorySeeds?.enabled);
    setValue(
      'daysLimit',
      model?.initialDiscoveryHistorySeeds?.daysLimit
        ? (model?.initialDiscoveryHistorySeeds?.daysLimit?.toString() as NumericDaysLimit)
        : model?.initialDiscoveryHistorySeeds?.afterDate
          ? 'custom'
          : 'all',
    );
    setValue(
      'afterDate',
      model?.initialDiscoveryHistorySeeds?.afterDate
        ? parse(
            model?.initialDiscoveryHistorySeeds?.afterDate,
            'yyyy-MM-dd',
            new Date(),
          )
        : null,
    );
  }, [
    prevInitialDiscoveryHistorySeeds,
    model?.initialDiscoveryHistorySeeds,
    model?.initialDiscoveryHistorySeeds?.afterDate,
    model?.initialDiscoveryHistorySeeds?.daysLimit,
    model?.initialDiscoveryHistorySeeds?.enabled,
    setValue,
  ]);

  return (
    <div className='border-t border-default pt-xs'>
      <div className='py px-sm'>
        <FormSwitch
          control={control}
          name='enabled'
          label='Use previously discovered devices as seeds'
          description={
            <>
              Previously discovered devices are listed in the{' '}
              <Link to='/management/discovery-history'>
                Discovery history table.
              </Link>
            </>
          }
        />
        <div className='flex gap-2.5 mt-xs pl-12'>
          <div className='w-60'>
            <FormSelect.Mandatory
              control={control}
              name='daysLimit'
              disabled={!enabled}
              options={[
                { label: 'Use entire history table', value: 'all' },
                { label: 'Devices discovered in last 7 days', value: '7' },
                { label: 'Devices discovered in last 14 days', value: '14' },
                { label: 'Devices discovered in last 30 days', value: '30' },
                { label: 'Devices discovered in last 3 months', value: '90' },
                {
                  label: `Devices discovered after ${horizontalEllipsis}`,
                  value: 'custom',
                },
              ]}
            />
          </div>
          <div className='w-60'>
            {daysLimit === 'custom' && (
              <FormDatepicker
                control={control}
                name='afterDate'
                className='w-60'
                disabled={!enabled}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
